import React, { useMemo, useState } from 'react'

import { Alert, Box, Button, CardHeader, Chip, LinearProgress, Stack, Tooltip, Typography } from '@mui/material'
import CustomerFeaturesResetModal from 'features/customers/components/features/CustomerFeaturesResetModal'
import CustomerFeaturesSaveBar from 'features/customers/components/features/CustomerFeaturesSaveBar'
import CustomerFeaturesTable from 'features/customers/components/features/CustomerFeaturesTable'
import { useCustomerFeatureChanges } from 'features/customers/hooks/useCustomerFeatureChanges'
import { useCustomerFeaturesData } from 'features/customers/hooks/useCustomerFeaturesData'
import { useCustomerFeaturesModify } from 'features/customers/hooks/useCustomerFeaturesModify'
import { useCustomerId } from 'features/customers/hooks/useCustomerId'
import { useFeatureEdits } from 'features/customers/hooks/useFeatureEdits'
import { buildFeatureRows, type FeatureFilter, filterCounts, setByPerson } from 'features/customers/utils/featureRows'

import LoadingView from 'components/LoadingView'

const FILTERS: { value: FeatureFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'overridden', label: 'Overridden' },
  { value: 'differs', label: 'Differs from plan' },
  { value: 'person', label: 'Set by a person' },
]

const EMPTY_LOG = { changes: [], overridden: [] }

const CustomerFeatures = () => {
  const customerId = useCustomerId()
  const { data, isLoading, planName } = useCustomerFeaturesData()
  const { data: changes, isError, isPending: changesPending } = useCustomerFeatureChanges()
  const { mutate: save, isPending: saving } = useCustomerFeaturesModify()

  const [filter, setFilter] = useState<FeatureFilter>('all')
  // The old array response means "we cannot tell", not "nothing is stored".
  const noProvenance = isError || Array.isArray(changes)
  const log = changes ?? EMPTY_LOG

  const groups = useMemo(() => buildFeatureRows(data, log, filter), [data, log, filter])
  const allRows = useMemo(() => buildFeatureRows(data, log).flatMap(group => group.rows), [data, log])

  const { edits, hasEdits, change, discard, payload, invalid, note, setNote, resetOpen, openReset, closeReset } =
    useFeatureEdits(allRows)
  const counts = filterCounts(allRows)
  const overrides = allRows.filter(row => row.overridden)
  const byPeople = overrides.filter(setByPerson).length

  const handleSave = () => save({ payload, note: note.trim() || undefined }, { onSuccess: discard })
  // The modal counts overrides from the log, and edits survive a reset to be re-applied on save.
  const resetBlockedBecause = noProvenance
    ? 'Reset needs the change history, which could not be loaded'
    : hasEdits
      ? 'Save or discard your changes first — they would be re-applied after the reset'
      : undefined

  return (
    <LoadingView loading={isLoading || changesPending}>
      <CardHeader
        action={
          <Tooltip title={resetBlockedBecause ?? ''}>
            <span>
              <Button disabled={!!resetBlockedBecause} onClick={openReset}>
                Reset to plan defaults
              </Button>
            </span>
          </Tooltip>
        }
        title="What this customer has"
      />

      <Stack
        direction="row"
        spacing={1}
        sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}
        useFlexGap>
        {FILTERS.filter(({ value }) => !noProvenance || value === 'all' || value === 'differs').map(
          ({ value, label }) => (
            <Chip
              aria-pressed={filter === value}
              clickable
              component="button"
              color={filter === value ? 'primary' : 'default'}
              key={value}
              label={
                <>
                  {label}
                  <Box component="span" sx={{ ml: 0.75, opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>
                    {counts[value]}
                  </Box>
                </>
              }
              onClick={() => setFilter(value)}
              variant={filter === value ? 'filled' : 'outlined'}
            />
          ),
        )}
      </Stack>

      {noProvenance && (
        <Alert severity="warning" sx={{ mx: 2, mt: 2 }}>
          We could not load the change history, so this page cannot say who set a value or which values are stored on
          the customer rather than coming from the plan. The values themselves are correct, but resetting and clearing
          individual overrides are unavailable until it loads.
        </Alert>
      )}

      {saving && <LinearProgress />}

      <Box
        sx={{
          pb: hasEdits ? 12 : 0,
          opacity: saving ? 0.5 : 1,
          pointerEvents: saving ? 'none' : 'auto',
          transition: 'opacity 150ms',
        }}>
        <CustomerFeaturesTable edits={edits} groups={groups} onChange={change} showQuotas={filter === 'all'} />
      </Box>

      {!groups.some(group => group.rows.length) && (
        <Typography color="text.secondary" sx={{ p: 2 }} variant="body2">
          Nothing matches this filter.
        </Typography>
      )}

      {hasEdits && (
        <CustomerFeaturesSaveBar
          count={Object.keys(edits).length}
          invalid={invalid}
          note={note}
          onDiscard={discard}
          onNoteChange={setNote}
          onSave={handleSave}
          saving={saving}
        />
      )}

      <CustomerFeaturesResetModal
        byPeople={byPeople}
        key={customerId}
        onClose={closeReset}
        open={resetOpen}
        overrides={overrides.length}
        planName={planName}
      />
    </LoadingView>
  )
}

export default CustomerFeatures
